import { EventEmitter } from '@angular/core';

/**
 * Status level for admin panel indicators
 */
export type AdminPanelStatusLevel = 'good' | 'warning' | 'error' | 'none';

/**
 * Status information for an admin panel
 */
export interface AdminPanelStatus {
  /** The status level determines the icon and color */
  status: AdminPanelStatusLevel;
  /** Tooltip text shown on hover */
  tooltip: string;
}

/**
 * Interface for admin panel components that can report their status.
 *
 * Components implementing this interface can calculate and emit their own
 * status (good/warning/error/none) based on their loaded data. The parent
 * administration component listens for these events and updates the
 * navigation indicators.
 *
 * @example
 * ```typescript
 * export class AIConfigurationComponent implements IAdminPanel {
 *   @Output() statusChange = new EventEmitter<AdminPanelStatus>();
 *
 *   private emitStatus(): void {
 *     const status = this.calculateStatus();
 *     this.statusChange.emit(status);
 *   }
 *
 *   private calculateStatus(): AdminPanelStatus {
 *     if (!this.selectedOrganization?.IzzyAIConfigurationID) {
 *       return { status: 'warning', tooltip: 'No AI configuration selected' };
 *     }
 *     if (this.missingCredentials.length > 0) {
 *       return { status: 'error', tooltip: 'Missing required credentials' };
 *     }
 *     return { status: 'good', tooltip: 'AI configuration ready' };
 *   }
 * }
 * ```
 */
export interface IAdminPanel {
  /**
   * Event emitter for status changes.
   * Components should emit whenever their status may have changed
   * (e.g., after loading data, after user makes changes).
   */
  statusChange: EventEmitter<AdminPanelStatus>;
}

/**
 * Default status for panels that don't have specific status logic
 */
export const DEFAULT_ADMIN_PANEL_STATUS: AdminPanelStatus = {
  status: 'none',
  tooltip: ''
};
